
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from "@/hooks/use-toast";
import { useFileUpload } from './useFileUpload';
import { useUtilityValidation } from './useUtilityValidation';
import { FormData } from '@/types/form';

export const useSubmissionForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submissionFolderId] = useState(() => crypto.randomUUID());
  const { uploadedFiles, handleFileUpload } = useFileUpload();

  const [formData, setFormData] = useState<FormData>({
    street: '',
    number: '',
    postcode: '',
    squareFootage: '',
    occupants: '',
    additionalComments: '',
    gasUsage: '',
    gasRate: '',
    gasStartDate: '',
    gasEndDate: '',
    gasPlanName: '',
    gasAnnualConsumption: '',
    gasPaymentSchedule: '',
    gasStandingCharges: '',
    electricityUsage: '',
    electricityRate: '',
    electricityStartDate: '',
    electricityEndDate: '',
    electricityPlanName: '',
    electricityAnnualConsumption: '',
    electricityPaymentSchedule: '',
    electricityStandingCharges: '',
    waterStartDate: '',
    waterEndDate: '',
    waterTotalCharge: '',
    waterVolumeCharge: '',
    age: '',
    referralSource: '',
    tankInsulation: '',
    drafts: '',
    openChimney: '',
    existingWaterHardware: '',
    showerheadType: '',
  });

  const { validateUtilityInputs, validatePropertyDetails } = useUtilityValidation(formData, uploadedFiles);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value: string, name: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submission started');
    console.log('Current form data:', formData);
    console.log('Uploaded files:', uploadedFiles);
    
    if (!validatePropertyDetails()) {
      toast({
        title: "Missing property details",
        description: "Please fill in all property details fields.",
        variant: "destructive",
      });
      return;
    }

    const utilities = ['gas', 'electricity', 'water'] as const;
    for (const utility of utilities) {
      console.log(`Validating ${utility} inputs...`);
      if (!validateUtilityInputs(utility)) {
        toast({
          title: `Missing ${utility} information`,
          description: `Please either upload a ${utility} bill or fill in all ${utility} details.`,
          variant: "destructive",
        });
        return;
      }
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please log in to submit your data.",
          variant: "destructive",
        });
        return;
      }

      const submissionData = {
        "Report ID": submissionFolderId,
        user_id: user.id,
        street: formData.street,
        number: formData.number,
        postcode: formData.postcode,
        square_footage: formData.squareFootage,
        occupants: formData.occupants,
        gas_usage: uploadedFiles.gas ? null : formData.gasUsage,
        gas_rate: uploadedFiles.gas ? null : formData.gasRate,
        gas_start_date: uploadedFiles.gas ? null : formData.gasStartDate || null,
        gas_end_date: uploadedFiles.gas ? null : formData.gasEndDate || null,
        gas_plan_name: uploadedFiles.gas ? null : formData.gasPlanName,
        gas_annual_consumption: uploadedFiles.gas ? null : (formData.gasAnnualConsumption ? Number(formData.gasAnnualConsumption) : null),
        gas_payment_schedule: uploadedFiles.gas ? null : formData.gasPaymentSchedule,
        gas_standing_charges: uploadedFiles.gas ? null : (formData.gasStandingCharges ? Number(formData.gasStandingCharges) : null),
        electricity_usage: uploadedFiles.electricity ? null : formData.electricityUsage,
        electricity_rate: uploadedFiles.electricity ? null : formData.electricityRate,
        electricity_start_date: uploadedFiles.electricity ? null : formData.electricityStartDate || null,
        electricity_end_date: uploadedFiles.electricity ? null : formData.electricityEndDate || null,
        electricity_plan_name: uploadedFiles.electricity ? null : formData.electricityPlanName,
        electricity_annual_consumption: uploadedFiles.electricity ? null : (formData.electricityAnnualConsumption ? Number(formData.electricityAnnualConsumption) : null),
        electricity_payment_schedule: uploadedFiles.electricity ? null : formData.electricityPaymentSchedule,
        electricity_standing_charges: uploadedFiles.electricity ? null : (formData.electricityStandingCharges ? Number(formData.electricityStandingCharges) : null),
        water_start_date: uploadedFiles.water ? null : formData.waterStartDate || null,
        water_end_date: uploadedFiles.water ? null : formData.waterEndDate || null,
        additional_comments: formData.additionalComments,
        tank_insulation: formData.tankInsulation || null,
        drafts: formData.drafts || null,
        open_chimney: formData.openChimney || null,
        existing_water_hardware: formData.existingWaterHardware,
        showerhead_type: formData.showerheadType || null,
        age: formData.age,
        referral_source: formData.referralSource,
        water_total_charge: uploadedFiles.water ? null : formData.waterTotalCharge,
        water_volume_charge: uploadedFiles.water ? null : formData.waterVolumeCharge,
      };

      console.log('Submitting data to Supabase:', submissionData);
      const { error } = await supabase
        .from('user_inputs')
        .insert(submissionData);

      if (error) throw error;
      
      toast({
        title: "Success!",
        description: "Your submission has been received. Generating insights...",
      });
      
      // Update user profile status
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ status: 'input_unprocessed' })
        .eq('id', user.id);

      if (updateError) throw updateError;

      navigate('/submission');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Error submitting form",
        description: "There was a problem submitting your data. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    formData,
    uploadedFiles,
    handleInputChange,
    handleSelectChange,
    handleFileUpload,
    handleSubmit
  };
};
